import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';

export const PageTransition = ({ children }) => {
  const location = useLocation();
  const [transitioning, setTransitioning] = useState(false);
  const videoRef = useRef(null);
  const prevPathRef = useRef(location.pathname);
  const isFirstRender = useRef(true);
  const timerRef = useRef(null);

  const finish = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setTransitioning(false);
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevPathRef.current = location.pathname;
      return;
    }
    if (location.pathname === prevPathRef.current) return;
    prevPathRef.current = location.pathname;
    if (location.pathname === '/admin') return;

    setTransitioning(true);
  }, [location.pathname]);

  useEffect(() => {
    if (!transitioning) return;
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.playbackRate = 2.6;
      videoRef.current.play().catch(() => {});
    }
    timerRef.current = setTimeout(finish, 2500);
    return () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };
  }, [transitioning, finish]);

  return (
    <>
      {transitioning && (
        <div className="fixed inset-0 z-[100] bg-black" style={{ pointerEvents: 'auto' }}>
          <video
            ref={videoRef}
            playsInline
            preload="auto"
            muted
            className="w-full h-full object-cover"
            onEnded={finish}
          >
            <source src="/media/transition.mp4" type="video/mp4" />
          </video>
          <button
            type="button"
            aria-label="Skip transition"
            data-testid="skip-transition-btn"
            onClick={finish}
            className="absolute top-6 right-6 flex items-center gap-2 px-3.5 py-2 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-md border border-white/20 hover:border-gold-500/60 text-white/80 hover:text-gold-300 text-xs uppercase tracking-widest font-semibold transition-all duration-200 shadow-lg opacity-70 hover:opacity-100"
          >
            Skip <X size={14} />
          </button>
        </div>
      )}
      {children}
    </>
  );
};
